import { APIProvider, Map } from '@vis.gl/react-google-maps'
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import MapFeatureLayer from './mapFeatureLayer'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { OfficeCitiesMappingMapProps, OfficeCitiesMappingMapRef, RegionDetailsEntity } from '../../../interface/offices'
import PrimePaginator from '../../../components/prime-paginator'
import { PaginatorPageChangeEvent } from 'primereact/paginator'

const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
const mapId = process.env.REACT_APP_GOOGLE_MAPS_MAPID || ''

const OfficeCitiesMappingMap = forwardRef<OfficeCitiesMappingMapRef, OfficeCitiesMappingMapProps>(function OfficeCitiesMappingMap({
    currentOffice,
    assignments,
    initialSelectedCities
}: OfficeCitiesMappingMapProps, ref: React.ForwardedRef<OfficeCitiesMappingMapRef>) {
    const [ selectedAreas, setSelectedAreas ] = useState<RegionDetailsEntity[]>([]);
    const [ globalFilterValue, setGlobalFilterValue ] = useState<string>('');
    const [ pageNumber, setPageNumber ] = useState(0);
    const [ pageSize, setPageSize ] = useState(10);

    useImperativeHandle(ref, () => ({
        getSelectedAreas: () => selectedAreas
    }), [ selectedAreas ]);

    useEffect(() => {
        if (initialSelectedCities?.length) {
            setSelectedAreas(initialSelectedCities)
        }
    }, [ initialSelectedCities ])

    useEffect(() => {
        setPageNumber(0);
    }, [ globalFilterValue ]);

    const onPageChange = (event: PaginatorPageChangeEvent) => {
        setPageNumber(event.page);
        setPageSize(event.rows);
    };

    const normalizedSearch = globalFilterValue.trim().toLowerCase();
    const filteredAreas = normalizedSearch
        ? selectedAreas.filter((area) => area.placeName?.toLowerCase().includes(normalizedSearch))
        : selectedAreas;

    const totalRecords = filteredAreas.length;

    useEffect(() => {
        const maxPageNumber = totalRecords > 0 ? Math.ceil(totalRecords / pageSize) - 1 : 0;

        if (pageNumber > maxPageNumber) {
            setPageNumber(maxPageNumber);
        }
    }, [ pageNumber, pageSize, totalRecords ]);

    const paginatedData = filteredAreas.slice(
        pageNumber * pageSize,
        pageNumber * pageSize + pageSize
    );

    return (
        <>
            <div className="col-md-9">
                <h4 className="mb-3 fw-bold">Office Coverage Map</h4>
                <APIProvider apiKey={apiKey} libraries={[ 'places' ]}>
                    <Map
                        mapId={mapId}
                        defaultCenter={{ lat: 20.5937, lng: 78.9629 }}
                        defaultZoom={5}
                        mapTypeControl={false}
                        tilt={0}
                        heading={0}
                        style={{ width: '100%', height: 'calc(100vh - 100px)', border: '1px solid black' }}
                    >
                        <MapFeatureLayer
                            currentOffice={currentOffice}
                            assignments={assignments}
                            initialSelectedCities={selectedAreas}
                            onSelectedAreasChange={setSelectedAreas}
                        />
                    </Map>
                </APIProvider>
            </div>
            <div className="col-md-3">
                <h4 className="mb-3 fw-bold">Office Coverage List</h4>
                <div className="mb-3 position-relative">
                    <input
                        type="text"
                        className="form-control form-control-lg searchInput"
                        placeholder="Search by place name"
                        value={globalFilterValue}
                        onChange={(e) => setGlobalFilterValue(e.target.value)}
                        style={{ paddingRight: globalFilterValue ? '45px' : '12px' }}
                    />
                    {globalFilterValue && (
                        <i
                            className="pi pi-times"
                            style={{
                                position: "absolute",
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                cursor: "pointer",
                                zIndex: 500
                            }}
                            onClick={() => setGlobalFilterValue('')}
                        />
                    )}
                </div>
                {/* <div style={{ height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}> */}
                <DataTable
                    // value={selectedAreas}
                    scrollable
                    value={paginatedData}
                    scrollHeight="calc(100vh - 250px)"
                    className="custom-datatable"
                    emptyMessage="No data found."
                >
                    <Column
                        field="placeName"
                        header="Place Name"
                        style={{ width: '70%' }}
                    />
                    <Column
                        header="Type"
                        headerStyle={{ paddingLeft: '15px' }}
                        bodyStyle={{ paddingLeft: '15px' }}
                        style={{ width: '20%' }}
                        body={(rowData) => {
                            if (rowData.type === 'LOCALITY') return 'City';
                            if (rowData.type === 'ADMINISTRATIVE_AREA_LEVEL_1') return 'State';
                            if (rowData.type === 'ADMINISTRATIVE_AREA_LEVEL_2') return 'Division';
                            if (rowData.type === 'COUNTRY') return 'Country';
                            return rowData.type;
                        }}
                    />
                    <Column
                        header="Action"
                        style={{ width: '10%', textAlign: 'right', paddingLeft: '5px' }}
                        bodyStyle={{ textAlign: 'right', paddingLeft: '5px' }}
                        body={(rowData) => (
                            <div
                                className="cursor-pointer"
                                title="Delete"
                                onClick={() => {
                                    setSelectedAreas(prev =>
                                        prev.filter(area => {
                                            if (rowData.type === 'LOCALITY') {
                                                return !(
                                                    area.type === 'LOCALITY' &&
                                                    area.city_providerId === rowData.city_providerId
                                                );
                                            }
                                            if (rowData.type === 'ADMINISTRATIVE_AREA_LEVEL_1') {
                                                return !(
                                                    area.type === 'ADMINISTRATIVE_AREA_LEVEL_1' &&
                                                    area.state_providerId === rowData.state_providerId
                                                );
                                            }
                                            if (rowData.type === 'ADMINISTRATIVE_AREA_LEVEL_2') {
                                                return !(
                                                    area.type === 'ADMINISTRATIVE_AREA_LEVEL_2' &&
                                                    area.division_providerId === rowData.division_providerId
                                                );
                                            }
                                            if (rowData.type === 'COUNTRY') {
                                                return !(
                                                    area.type === 'COUNTRY' &&
                                                    area.country_providerId === rowData.country_providerId
                                                );
                                            }
                                            return true;
                                        })
                                    );
                                }}
                            >
                                <img src="assets/images/delete-icon.svg" alt="Delete" />
                            </div>
                        )}
                    />
                </DataTable>
                {/* </div> */}
                {totalRecords > 0 && (
                    <PrimePaginator
                        onPageChange={onPageChange}
                        totalRecords={totalRecords}
                        pageNumber={pageNumber}
                        pageSize={pageSize}
                    />
                )}
            </div >
        </>
    )
});

export default OfficeCitiesMappingMap
